import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PacienteDetalhes.css';

import { API_URL } from '../config';
import { formatDate, formatDateTime } from '../utils/formatDate';

function PacienteDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [paciente, setPaciente] = useState(null);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('identificacao');

  useEffect(() => {
    fetchPacienteDetalhes();
  }, [id]);

  const fetchPacienteDetalhes = async () => {
    setLoading(true);
    setError('');
    try {
      const [pacienteRes, solicitacoesRes] = await Promise.all([
        axios.get(`${API_URL}/pacientes/${id}`),
        axios.get(`${API_URL}/pacientes/${id}/solicitacoes`)
      ]);
      setPaciente(pacienteRes.data);
      setSolicitacoes(solicitacoesRes.data);
    } catch (err) {
      setError('Erro ao carregar dados do paciente: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // formatDate e formatDateTime importados de ../utils/formatDate

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return '-';
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return `${idade} anos`;
  };

  if (loading) {
    return <div className="loading">Carregando dados do paciente...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error">{error}</p>
        <button onClick={() => navigate('/pacientes')} className="back-button">
          Voltar para Pacientes
        </button>
      </div>
    );
  }

  if (!paciente) {
    return <div className="loading">Paciente não encontrado</div>;
  }

  const normalizeId = (str) => str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s/g, '-');

  // Mapa de nomes legíveis para campos do banco
  const labelMap = {
    'ID': 'Código', 'NOME': 'Nome', 'NOMESOCIAL': 'Nome Social',
    'CPF': 'CPF', 'RG': 'RG', 'CNS': 'CNS', 'IDENTIDADE': 'Identidade',
    'DATANASCIMENTO': 'Data de Nascimento', 'SEXO': 'Sexo', 'ESTADOCIVIL': 'Estado Civil',
    'NATURALIDADE': 'Naturalidade', 'NACIONALIDADE': 'Nacionalidade', 'RACA': 'Raça', 'ETNIA': 'Etnia',
    'EMAIL': 'E-mail', 'TELEFONE': 'Telefone', 'CELULAR': 'Celular',
    'TELEFONEPRINCIPAL': 'Tel. Principal', 'TELEFONECELULAR': 'Celular',
    'TELEFONECOMERCIAL': 'Tel. Comercial', 'TELEFONERECADO': 'Tel. Recado',
    'ENDERECO': 'Endereço', 'NUMERO': 'Número', 'COMPLEMENTO': 'Complemento',
    'BAIRRO': 'Bairro', 'CIDADE_NOME': 'Cidade', 'CIDADE_UF': 'UF',
    'ESTADO': 'Estado', 'CEP': 'CEP', 'PAIS': 'País', 'LOGRADOURO': 'Logradouro',
    'UF': 'UF', 'COD_IBGE': 'Cód. IBGE',
    'NOMEMAE': 'Nome da Mãe', 'NOMEPAI': 'Nome do Pai', 'MAE': 'Mãe', 'PAI': 'Pai',
    'PESO': 'Peso', 'ALTURA': 'Altura', 'TIPOSANGUINEO': 'Tipo Sanguíneo',
    'ALERGIAS': 'Alergias', 'MEDICAMENTOS': 'Medicamentos',
    'OBSERVACOES': 'Observações', 'OBSERVACAO': 'Observação',
    'PROFISSAO': 'Profissão', 'OCUPACAO': 'Ocupação',
    'EMPRESA': 'Empresa', 'EMPRESATRABALHO': 'Empresa de Trabalho',
    'CONVENIO': 'Convênio', 'NUMEROCONVENIO': 'Nº Convênio',
    'PLANO': 'Plano', 'PLANOSAUDE': 'Plano de Saúde',
    'VALIDADECONVENIO': 'Val. Convênio', 'CARTEIRINHA': 'Carteirinha',
    'RESPONSAVEL': 'Responsável', 'NOMERESPONSAVEL': 'Nome do Responsável',
    'CPFRESPONSAVEL': 'CPF Responsável', 'TELEFONERESPONSAVEL': 'Tel. Responsável',
    'DATACADASTRO': 'Data de Cadastro', 'DATAATUALIZACAO': 'Última Atualização',
    'ATIVO': 'Ativo', 'STATUS': 'Status', 'SITUACAO': 'Situação',
    'USUARIO': 'Usuário', 'USUARIOCADASTRO': 'Usuário Cadastro',
    'TELEFONE_CELULAR': 'Celular', 'TELEFONE_PRINCIPAL': 'Tel. Principal',
    'EMAIL_PRINCIPAL': 'E-mail Principal', 'NOME_COMPLETO': 'Nome Completo',
    'DATA_NASCIMENTO': 'Data de Nascimento', 'ESTADO_CIVIL': 'Estado Civil',
    'TIPO_SANGUINEO': 'Tipo Sanguíneo', 'NOME_MAE': 'Nome da Mãe', 'NOME_PAI': 'Nome do Pai',
  };

  const getLabel = (key) => labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Organizar campos por categoria
  const categorizarCampos = () => {
    const categorias = {
      'Identificação': ['ID', 'NOME', 'NOMESOCIAL', 'CPF', 'RG', 'CNS', 'IDENTIDADE'],
      'Dados Pessoais': ['DATANASCIMENTO', 'SEXO', 'ESTADOCIVIL', 'NATURALIDADE', 'NACIONALIDADE', 'RACA', 'ETNIA'],
      'Contato': ['EMAIL', 'TELEFONE', 'CELULAR', 'TELEFONEPRINCIPAL', 'TELEFONECELULAR', 'TELEFONECOMERCIAL', 'TELEFONERECADO'],
      'Endereço': ['ENDERECO', 'NUMERO', 'COMPLEMENTO', 'BAIRRO', 'CIDADE_NOME', 'CIDADE_UF', 'ESTADO', 'CEP', 'PAIS', 'LOGRADOURO', 'UF', 'COD_IBGE'],
      'Filiação': ['NOMEMAE', 'NOMEPAI', 'MAE', 'PAI'],
      'Dados Clínicos': ['PESO', 'ALTURA', 'TIPOSANGUINEO', 'ALERGIAS', 'MEDICAMENTOS', 'OBSERVACOES', 'OBSERVACAO'],
      'Profissional': ['PROFISSAO', 'OCUPACAO', 'EMPRESA', 'EMPRESATRABALHO'],
      'Convênio': ['CONVENIO', 'NUMEROCONVENIO', 'PLANO', 'PLANOSAUDE', 'VALIDADECONVENIO', 'CARTEIRINHA'],
      'Responsável': ['RESPONSAVEL', 'NOMERESPONSAVEL', 'CPFRESPONSAVEL', 'TELEFONERESPONSAVEL'],
      'Dados Administrativos': ['DATACADASTRO', 'DATAATUALIZACAO', 'ATIVO', 'STATUS', 'SITUACAO', 'USUARIO', 'USUARIOCADASTRO'],
    };

    const camposCategorizados = {};
    const camposNaoCategorizados = [];

    Object.keys(paciente).forEach((key) => {
      let encontrado = false;
      for (const [categoria, campos] of Object.entries(categorias)) {
        if (campos.some(c => key.toUpperCase().includes(c))) {
          if (!camposCategorizados[categoria]) {
            camposCategorizados[categoria] = [];
          }
          camposCategorizados[categoria].push(key);
          encontrado = true;
          break;
        }
      }
      if (!encontrado) {
        camposNaoCategorizados.push(key);
      }
    });

    if (camposNaoCategorizados.length > 0) {
      camposCategorizados['Outros'] = camposNaoCategorizados;
    }

    return camposCategorizados;
  };

  const formatarValor = (key, value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    } else if (key.includes('DATA') || key.includes('NASCIMENTO') || key.includes('CADASTRO') || key.includes('ATUALIZACAO') || key.includes('VALIDADE')) {
      return formatDate(value);
    } else if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    } else if (typeof value === 'number') {
      return value.toString();
    } else {
      return String(value);
    }
  };

  const camposCategorizados = categorizarCampos();

  // Definir abas disponíveis baseadas nas categorias existentes
  const abas = [
    ...Object.keys(camposCategorizados).map(categoria => ({
      id: normalizeId(categoria),
      label: `${categoria}`,
      show: true
    })),
    { id: 'historico', label: '📑 Histórico de Solicitações', show: solicitacoes.length > 0 }
  ].filter(aba => aba.show);

  return (
    <div className="paciente-detalhes-page">
      <div className="page-header-detalhes">
        <button onClick={() => navigate('/pacientes')} className="back-button">
          ← Voltar
        </button>
        <h1>Detalhes do Paciente</h1>
      </div>

      {/* Sistema de Abas */}
      <div className="detail-tabs">
        {abas.map(aba => (
          <button
            key={aba.id}
            onClick={() => setAbaAtiva(aba.id)}
            className={`detail-tab ${abaAtiva === aba.id ? 'active' : ''}`}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {/* Abas de Dados do Paciente */}
      {Object.entries(camposCategorizados).map(([categoria, campos]) => {
        const categoriaId = normalizeId(categoria);
        return abaAtiva === categoriaId ? (
          <div key={categoria} className="detalhes-grid">
        {Object.entries(camposCategorizados).filter(([cat]) => cat === categoria).map(([categoria, campos]) => (
          <div key={categoria} className="info-card">
            <h2>📋 {categoria}</h2>
            <div className="info-grid">
              {campos.map((key) => {
                const displayValue = formatarValor(key, paciente[key]);
                return (
                  <div key={key} className={displayValue.length > 50 ? 'info-item full-width' : 'info-item'}>
                    <label>{getLabel(key)}</label>
                    <span>{displayValue}</span>
                  </div>
                );
              })}
              {categoria === 'Dados Pessoais' && paciente.DATANASCIMENTO && (
                <div className="info-item">
                  <label>Idade</label>
                  <span>{calcularIdade(paciente.DATANASCIMENTO)}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
        ) : null;
      })}

      {/* Aba: Histórico de Solicitações */}
      {abaAtiva === 'historico' && (
      <div className="solicitacoes-section">
        <h2>📋 Histórico de Solicitações ({solicitacoes.length})</h2>
        {solicitacoes.length > 0 ? (
          <div className="table-responsive">
            <table className="solicitacoes-table">
              <thead>
                <tr>
                  <th>Protocolo</th>
                  <th>Data</th>
                  <th>Hora</th>
                  <th>Valor Total</th>
                </tr>
              </thead>
              <tbody>
                {solicitacoes.map((sol) => (
                  <tr
                    key={sol.ID}
                    onClick={() => navigate(`/protocolo/${sol.PROTOCOLO}`)}
                    style={{ cursor: 'pointer' }}
                    className="clickable-row"
                  >
                    <td><strong>{sol.PROTOCOLO}</strong></td>
                    <td>{formatDate(sol.DATA)}</td>
                    <td>{sol.HORARIO || '-'}</td>
                    <td>{formatCurrency(sol.TOTAL)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-data">Nenhuma solicitação encontrada para este paciente.</p>
        )}
      </div>
      )}
    </div>
  );
}

export default PacienteDetalhes;
